import { Box, Flex, Text } from "@chakra-ui/react";
import { Row, Col } from "antd";
import Field from "components/Input/Field/index";
import { useForm } from "react-hook-form";
import OutlineButton from "components/Button/OutlineButton";
import { useState, useEffect } from "react";
import { MdLocalPrintshop } from "react-icons/md";
import { BsPlusLg } from "react-icons/bs";
import TableItensCotados from "./components/TableItensCotados/index";
import ModalItemSerCotado from "./components/ModalItemSerCotado";
import SelectStatusOrcamento from "components/Input/SelectStatusOrcamento/index";
import useSaveOrcamento from "core/features/orcamento/hooks/useSaveOrcamento";
import { OrcamentoDadosForm } from "features/orcamento/typings";
import history from "infra/helpers/history";
import PrimaryButton from "components/Button/PrimaryButton";
import { showSuccess, showErrorAxios } from "infra/helpers/alerta";
import { useListarItensCotados } from "core/features/orcamento/hooks/useListarItensCotados";
import moment from "moment";
import useItemsOrcamentoPdf from "core/features/orcamento/hooks/useItemsOrcamento";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import PDFItensOrcamento from "./components/PDFItensOrcamento/index";
import { AssinaturasItem } from "components/Relatorio/FormAssinaturas";
import ModalAssinaturasRelatorio from "components/Relatorio/ModalAssinaturas";
import { useLoading } from "components/GlobalLoading/index";
import { useBreadcrumb } from "core/features/breadcrumbs/index";

export default function DadosOrcamento({ id }: { id: string }) {
  const defaultValues: OrcamentoDadosForm = {
    descricao: "",
    statusOrcamentoId: undefined,
    orcamentoItems: [],
  };

  useBreadcrumb([
    { name: "Administração", url: "/administracao" },
    { name: "Orçamento", url: "/administracao/orcamento" },
    {
      name: "Dados",
    },
  ]);
  const [modalAssinaturasOpen, setModalAssinaturasOpen] = useState(false);
  const [indexEdit, setIndexEdit] = useState<number | undefined>();
  const [openModal, setOpenModal] = useState(false);
  const { control, reset, handleSubmit } = useForm<OrcamentoDadosForm>({
    defaultValues,
  });

  const {
    data: dataItensCotados,
    isLoading: isLoadingItensCotados,
    refetch,
  } = useListarItensCotados(id);

  const { isLoading: isSaveLoading, mutate: handleSave } = useSaveOrcamento(id);

  const {
    refetch: refetchPdf,
    isLoading: isLoadingPdf,
    isFetching: isFetchingPdf,
  } = useItemsOrcamentoPdf(id);

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Items a serem cotados - ${moment().format("DD_MM_YYYY")}`,
    Component: PDFItensOrcamento,
    loading: isLoadingPdf || isFetchingPdf,
  });

  const onSubmitAssinaturas = (assinaturas: AssinaturasItem[]) => {
    refetchPdf().then(({ data: dataExport }) => {
      if (dataExport) {
        onExportPdf({ data: { ...dataExport, assinaturas } });
        setModalAssinaturasOpen(false);
      }
    });
  };

  useEffect(() => {
    if (Number.isInteger(indexEdit)) {
      setOpenModal(true);
    }
  }, [indexEdit]);

  useEffect(() => {
    if (dataItensCotados) {
      reset({
        id: dataItensCotados.id,
        descricao: dataItensCotados.descricao,
        statusOrcamentoId: dataItensCotados.statusOrcamentoId.id,
        orcamentoItems:
          dataItensCotados.orcamentoItems?.map((item) => ({
            id: item.id,
            nome: item.nome,
            quantidade: item.quantidade,
            unidadeMedidaId: item.unidadeMedidaId,
          })) ?? [],
      });
    }
  }, [dataItensCotados, reset]);

  const onSubmit = async (data: OrcamentoDadosForm) => {
    handleSave(data, {
      onSuccess: (result) => {
        showSuccess().then(() => {
          if (!id) {
            history.replace(`/administracao/orcamento/${result.data.id}`);
          } else {
            refetch();
          }
        });
      },
      onError: (e) => {
        showErrorAxios(e);
      },
    });
  };

  useLoading(isLoadingItensCotados);

  return (
    <>
      <form onSubmit={(ev) => handleSubmit(onSubmit)(ev)}>
        <Box>
          <Box as={Row} gutter={32} mb={6}>
            <Box as={Col} xs={16}>
              <Field
                control={control}
                name="descricao"
                label="Descrição"
                rules={{ required: true }}
              />
            </Box>
            <Box as={Col} xs={8}>
              <Field
                control={control}
                name="statusOrcamentoId"
                label="Status"
                render={({ field }) => (
                  <Flex alignItems="center">
                    <SelectStatusOrcamento {...field} />
                  </Flex>
                )}
              />
            </Box>
          </Box>

          <Flex>
            <Flex flex={1}>
              <Text mr="16px" fontSize={"18px"} fontWeight={"bold"} mb="32px">
                Itens a serem cotados
              </Text>
              <OutlineButton onClick={() => setOpenModal(true)} size="xs">
                <BsPlusLg />
                Adicionar
              </OutlineButton>
            </Flex>
            <OutlineButton
              onClick={() => setModalAssinaturasOpen(true)}
              size="xs"
            >
              <MdLocalPrintshop size={20} /> Imprimir
            </OutlineButton>
          </Flex>

          <Box>
            <Field
              name="orcamentoItems"
              control={control}
              render={({ field }) => (
                <>
                  {openModal ? (
                    <ModalItemSerCotado
                      isOpen={openModal}
                      isEdit={!!indexEdit}
                      onClose={() => {
                        setOpenModal(false);
                        setIndexEdit(undefined);
                      }}
                      defaultData={
                        Number.isInteger(indexEdit)
                          ? field.value[indexEdit!]
                          : undefined
                      }
                      onSaved={(orcamentoItems) => {
                        if (Number.isInteger(indexEdit)) {
                          const copy = [...field.value];

                          copy.splice(indexEdit!, 1, orcamentoItems);

                          field.onChange(copy);
                        } else {
                          field.onChange([...field.value, orcamentoItems]);
                        }
                        setIndexEdit(undefined);
                        setOpenModal(false);
                      }}
                    />
                  ) : null}

                  <TableItensCotados
                    data={field.value ?? []}
                    isLoading={false}
                    onDelete={(index: number) =>
                      field.onChange(
                        field.value.filter(
                          (_: any, _index: number) => _index !== index
                        )
                      )
                    }
                    onEdit={setIndexEdit}
                  />
                </>
              )}
            />
          </Box>

          <Flex mt="16px" gap={4} justifyContent={"flex-end"}>
            <OutlineButton
              onClick={() => history.push(`/administracao/orcamento`)}
            >
              Voltar
            </OutlineButton>
            <PrimaryButton type="submit" isLoading={isSaveLoading}>
              Salvar
            </PrimaryButton>
          </Flex>
        </Box>
      </form>

      {modalAssinaturasOpen ? (
        <ModalAssinaturasRelatorio
          isOpenModal={modalAssinaturasOpen}
          onClose={() => setModalAssinaturasOpen(false)}
          onSubmit={onSubmitAssinaturas}
        />
      ) : null}
    </>
  );
}
